export interface AudienceContextInsights {
  status: 'ready' | 'disabled' | 'unavailable';
  reason?: 'missing_campaign_owner';
  schema_version?: number;
  computed_at?: string;
  source_precedence?: 'original_then_context';
  coverage?: {
    total_hits: number;
    correlatable_hits: number;
    enriched_hits: number;
    partial_hits: number;
    iframe_hits: number;
    coverage_pct: number | null;
  };
  byPage?: { origin: string; path: string; scope: string; hits: number; unique_users: number }[];
  byQuery?: { origin: string; path: string; querystring: string; hits: number; unique_users: number }[];
}

const format = (value?: number) => (value ?? 0).toLocaleString();

export function AudienceContext({ context }: { context: AudienceContextInsights }) {
  if (context.status === 'unavailable') return <p className="text-xs text-amber-600 mt-4">Page context is unavailable because this campaign has no configured owner. Tag hit metrics remain available.</p>;
  if (context.status !== 'ready' || !context.coverage) return null;
  const coverage = context.coverage;
  return (
    <section className="mt-4 pt-4 border-t border-border" aria-label="Page context">
      <div className="text-xs font-semibold text-slate-500 uppercase mb-3">Page context</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3 text-sm">
        <div><strong>{coverage.coverage_pct != null ? `${coverage.coverage_pct.toFixed(1)}%` : '—'}</strong><p className="text-xs text-slate-500">of tag hits enriched</p><p className="text-xs text-slate-400">{format(coverage.enriched_hits)} / {format(coverage.total_hits)}</p></div>
        <div><strong>{format(coverage.correlatable_hits)}</strong><p className="text-xs text-slate-500">hits with an event ID</p></div>
        <div><strong>{format(coverage.partial_hits)}</strong><p className="text-xs text-slate-500">hits with partial context</p></div>
        <div><strong>{format(coverage.iframe_hits)}</strong><p className="text-xs text-slate-500">hits inside an iframe</p></div>
      </div>
      <p className="text-xs text-slate-400 mb-3">Page information is attached to existing tag hits. Older tags without an event ID cannot be matched. New context may take a few minutes to appear after Refresh.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-slate-400"><th className="py-1 font-medium">Page where the tag ran</th><th className="py-1 font-medium">Scope</th><th className="py-1 font-medium text-right">Hits</th><th className="py-1 font-medium text-right">Users</th></tr></thead>
          <tbody className="divide-y divide-border">
            {(context.byPage || []).map((row, i) => <tr key={i}>
              <td className="py-1 pr-3 text-slate-700 break-all">{row.origin}{row.path}</td>
              <td className="py-1 pr-3 text-slate-500">{row.scope === 'iframe' ? 'Iframe' : row.scope === 'top_level' ? 'Top page' : 'Unknown'}</td>
              <td className="py-1 text-right text-slate-700">{format(row.hits)}</td><td className="py-1 text-right text-slate-500">{format(row.unique_users)}</td>
            </tr>)}
            {!context.byPage?.length && <tr><td colSpan={4} className="py-2 text-slate-400">No page context matched in this period.</td></tr>}
          </tbody>
        </table>
      </div>
      {!!context.byQuery?.length && <details className="mt-4">
        <summary className="cursor-pointer text-xs font-semibold text-slate-500">Allowed query parameters</summary>
        <div className="overflow-x-auto mt-2"><table className="w-full text-xs">
          <thead><tr className="text-left text-slate-400"><th className="py-1 font-medium">Page and permitted parameters</th><th className="py-1 font-medium text-right">Hits</th><th className="py-1 font-medium text-right">Users</th></tr></thead>
          <tbody className="divide-y divide-border">{context.byQuery.map((row, i) => <tr key={i}>
            <td className="py-1 pr-3 max-w-xl break-all"><span className="text-slate-500">{row.origin}{row.path}</span><div className="text-slate-700">?{row.querystring.replace(/^\?/, '')}</div></td>
            <td className="py-1 text-right text-slate-700">{format(row.hits)}</td><td className="py-1 text-right text-slate-500">{format(row.unique_users)}</td>
          </tr>)}</tbody>
        </table></div>
      </details>}
      <p className="text-[11px] text-slate-400 mt-3">An iframe reports its own page. Its referrer is not treated as the visitor's previous page. Only parameters allowed by the campaign are displayed.</p>
      {context.computed_at && <p className="text-[11px] text-slate-400 mt-1">Context updated: {new Date(context.computed_at).toLocaleString()}</p>}
    </section>
  );
}
